import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useParams } from "react-router";
import { resolveIpAssets } from "~/theme/resolve-theme";

interface PvSectionProps {
  videoId?: string;
  previewSrc?: string;
}

export function PvSection({ videoId = "K-zA-XjmM0E", previewSrc }: PvSectionProps) {
  const { slug = "" } = useParams<{ slug: string }>();
  const assets = resolveIpAssets(slug);
  const resolvedPreviewSrc = previewSrc ?? assets.pvPreview;
  const [modal, setModal] = useState(false);
  const closeRef = useRef<HTMLButtonElement>(null);
  const playRef = useRef<HTMLButtonElement>(null);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (modal && closeRef.current) closeRef.current.focus();
  }, [modal]);

  // Mobile: 초기엔 opacity 0 으로 숨겨두고 스크롤하면 점점 노출되도록.
  useEffect(() => {
    const isMobile = () => window.matchMedia("(max-width: 767px)").matches;
    const onScroll = () => {
      const el = sectionRef.current;
      if (!el) return;
      if (!isMobile()) {
        el.style.opacity = "";
        return;
      }
      const scrollY = window.scrollY;
      el.style.opacity = String(Math.min(scrollY / 200, 1));
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const closeModal = () => {
    setModal(false);
    playRef.current?.focus();
  };

  return (
    <>
      <section
        ref={sectionRef}
        id="pv"
        className="relative z-[2] w-full aspect-[21/9] max-md:aspect-video overflow-hidden bg-black max-md:opacity-0"
      >
        {/* Background preview video */}
        <div className="absolute inset-0 pointer-events-none">
          <video
            src={resolvedPreviewSrc}
            autoPlay
            muted
            loop
            playsInline
            tabIndex={-1}
            className="absolute top-1/2 left-1/2 w-screen h-[56.25vw] min-h-dvh min-w-[177.78dvh] -translate-x-1/2 -translate-y-1/2 object-cover"
          />
        </div>

        {/* Dim overlay */}
        <div className="absolute inset-0 bg-white/45 z-[1]" />

        {/* Play button */}
        <div className="absolute inset-0 z-[2] flex flex-col items-center justify-center gap-5">
          <button
            ref={playRef}
            className="w-20 h-20 max-md:w-14 max-md:h-14 flex items-center justify-center transition-transform duration-[250ms] hover:scale-110"
            onClick={() => setModal(true)}
            aria-label="Play"
          >
            <svg
              viewBox="0 0 80 80"
              className="w-20 h-20 max-md:w-14 max-md:h-14 drop-shadow-[0_0_8px_rgba(0,0,0,0.2)]"
            >
              <path
                fillRule="evenodd"
                fill="white"
                d="M40 0a40 40 0 1 1 0 80A40 40 0 0 1 40 0zM33 26L54 40 33 54Z"
              />
            </svg>
          </button>
        </div>
      </section>

      {/* Modal — portaled to body to avoid z-index/overflow issues */}
      {modal &&
        createPortal(
          <div
            data-lenis-prevent
            className="fixed inset-0 z-[1000] bg-[rgba(10,20,30,0.88)] backdrop-blur-[8px] flex items-center justify-center animate-[pvModalFadeIn_0.25s_ease]"
            onClick={closeModal}
            onKeyDown={(e) => e.key === "Escape" && closeModal()}
          >
            <button
              ref={closeRef}
              className="absolute top-6 right-6 w-14 h-14 flex items-center justify-center border border-white/30 rounded-full text-white hover:bg-white/10 hover:border-white/60 transition-all"
              onClick={closeModal}
              aria-label="Close video"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                className="w-[48px] h-[48px]"
              >
                <path d="M18 6L6 18M6 6L18 18" strokeLinecap="round" />
              </svg>
            </button>
            <div
              className="w-[min(900px,90vw)] aspect-video rounded overflow-hidden shadow-[0_24px_80px_rgba(0,0,0,0.5)] animate-[pvModalScaleIn_0.3s_cubic-bezier(0.34,1.56,0.64,1)]"
              onClick={(e) => e.stopPropagation()}
            >
              <iframe
                src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`}
                title="PV"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full border-0"
              />
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
